import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { NewsletterForm } from '../NewsletterForm/NewsletterForm';
import { useExitIntent } from '../../hooks/useExitIntent';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import './style.less';

const POPUP_SHOWN_KEY = 'newsletter_popup_shown';
const POPUP_DISMISSED_KEY = 'newsletter_popup_dismissed';
const SUBSCRIBED_KEY = 'newsletter_subscribed';
const POPUP_DELAY = 30000; // 30 seconds before showing on first visit
const DISMISS_COOLDOWN = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Newsletter subscription popup component
 * Shows on exit intent or after delay on first visit
 */
export const NewsletterPopup = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const shouldShowPopup = useCallback(() => {
    // Don't show if already subscribed
    if (localStorage.getItem(SUBSCRIBED_KEY) === 'true') return false;

    // Don't show if dismissed recently (within 24 hours)
    const dismissed = localStorage.getItem(POPUP_DISMISSED_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_COOLDOWN) return false;

    return true;
  }, []);

  // Exit intent detection
  useExitIntent(() => {
    if (shouldShowPopup() && !isVisible) {
      setIsVisible(true);
    }
  });

  // First visit timer
  useEffect(() => {
    if (!localStorage.getItem(POPUP_SHOWN_KEY) && shouldShowPopup()) {
      const timer = setTimeout(() => {
        if (shouldShowPopup()) {
          setIsVisible(true);
          localStorage.setItem(POPUP_SHOWN_KEY, 'true');
        }
      }, POPUP_DELAY);
      return () => clearTimeout(timer);
    }
  }, [shouldShowPopup]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(POPUP_DISMISSED_KEY, Date.now().toString());
  }, []);

  const handleSuccess = useCallback(() => {
    // Close popup after a short delay to show success message
    setTimeout(() => setIsVisible(false), 2000);
  }, []);

  // Close on Escape key
  useEscapeKey(
    useCallback(() => {
      if (isVisible) {
        handleClose();
      }
    }, [isVisible, handleClose])
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="newsletter-popup__backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          {/* Modal */}
          <motion.div
            className="newsletter-popup__modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="newsletter-popup-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="newsletter-popup__close"
              onClick={handleClose}
              type="button"
              aria-label={t('newsletter.popup.close')}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>

            {/* Content */}
            <div className="newsletter-popup__content">
              <div className="newsletter-popup__icon">
                <i className="fas fa-envelope-open-text" aria-hidden="true"></i>
              </div>

              <h3 id="newsletter-popup-title" className="newsletter-popup__title">
                {t('newsletter.popup.title')}
              </h3>

              <p className="newsletter-popup__description">{t('newsletter.popup.description')}</p>

              <NewsletterForm variant="popup" onSuccess={handleSuccess} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
